import { Box, FormControl, SxProps, TextField } from '@mui/material';
import { defaultCurrency } from 'common/constants';
import { Currency } from 'common/types';
import { useCurrency } from 'core/hooks/useCurrency';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CurrencySelector from './CurrencySelector';

export type AmountDataEdit = {
  amount: number | '';
  currency: Currency;
  exchangeRate?: number | '';
};

interface InputProps {
  value?: number | string;
  onChange?: (value: number | '') => void;
  sx?: SxProps;
  title: string;
  disabled?: boolean;
}

export interface AmountBoxProps {
  value?: AmountDataEdit;
  onChange?: (value: AmountDataEdit) => void;
}

const handleInputChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  handleChange: (value: number | '') => void
) => {
  const parsedAmount = event.target.value ? parseFloat(event.target.value) : '';
  if (parsedAmount !== '' && !isNaN(parsedAmount)) {
    handleChange(parsedAmount);
  } else {
    handleChange('');
  }
};

const Input = ({ value, onChange, title, sx, disabled }: InputProps) => {
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange && handleInputChange(event, onChange);

  return (
    <FormControl sx={sx}>
      <TextField
        label={title}
        variant="standard"
        inputProps={{
          step: 'any',
          min: '0'
        }}
        type="number"
        value={value}
        onChange={handleAmountChange}
        disabled={disabled}
      />
    </FormControl>
  );
};

const AmountBox = ({ value, onChange }: AmountBoxProps) => {
  const { currency = defaultCurrency, getExchangeRate } = useCurrency();
  const [requestedCurrency, setRequestedCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(true);
  const [currentExchangeRate, setCurrentExchangeRate] = useState<number>(1);
  const handleChange = (value: AmountDataEdit) => {
    onChange?.(value);
  };
  const isDefaultCurrency = value?.currency === currency;
  const currentCurrency = value?.currency || currency;

  const defaultCurrencyValue = useMemo(() => {
    const amountValue = typeof value?.amount === 'number' ? value.amount : 0;
    const exchangeRateValue =
      typeof value?.exchangeRate === 'number' ? value.exchangeRate : 1;
    return isDefaultCurrency
      ? amountValue
      : Math.round(amountValue * exchangeRateValue * 100) / 100;
  }, [isDefaultCurrency, value]);

  const handleAmountChange = useCallback(
    (amountValue: number | '') => {
      handleChange({
        amount: amountValue,
        currency: value?.currency || currency,
        exchangeRate: value?.exchangeRate || currentExchangeRate
      });
    },
    [value, handleChange, currency]
  );

  const handleCurrencyChange = useCallback(
    (newCurrency: Currency) => {
      handleChange({
        amount: value?.amount || '',
        currency: newCurrency,
        exchangeRate: value?.exchangeRate || currentExchangeRate
      });
    },
    [value, handleChange, currency]
  );

  const handleExchangeRateChange = useCallback(
    (exchangeRate: number | '') => {
      handleChange({
        amount: value?.amount || '',
        currency: value?.currency || currency,
        exchangeRate: exchangeRate ? exchangeRate : undefined
      });
    },
    [value, handleChange, currency]
  );

  useEffect(() => {
    //todo: prevent request for existing expense
    const fetchRate = async () => {
      if (!isDefaultCurrency && requestedCurrency !== currentCurrency) {
        const exchangeRate = await getExchangeRate(currentCurrency, currency);
        setRequestedCurrency(currentCurrency);
        handleChange({
          amount: value?.amount || '',
          currency: currentCurrency,
          exchangeRate: exchangeRate
        });
      }
    };
    fetchRate();
  }, [currentCurrency]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Input
        title="Amount"
        value={value?.amount}
        onChange={handleAmountChange}
        sx={{ flexGrow: 1 }}
      />
      <CurrencySelector
        value={value?.currency}
        onChange={handleCurrencyChange}
      />
      {!isDefaultCurrency && (
        <>
          <Input
            title="Exchange rate"
            value={value?.exchangeRate}
            onChange={handleExchangeRateChange}
          />
          <Input title={currency} value={defaultCurrencyValue} disabled />
        </>
      )}
    </Box>
  );
};

export default AmountBox;
